import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { shiftApiService } from "@/lib/services";
import { toast } from "@/hooks/use-toast";
import { getCurrentUser, getCurrentShop } from "@/lib/auth";
import { indexedDBService } from "@/lib/db/indexed-db";

/**
 * Hook personalizado para manejar turnos
 * Maneja estado, loading, errores y lógica de UI
 */
// {MODIFICADO: Recibir isOfflineMode como parámetro}
export function useShift(
  externalUserId?: string,
  isOfflineMode: boolean = false,
) {
  const queryClient = useQueryClient();

  // Estado para controlar UI
  const [showOpenShiftModal, setShowOpenShiftModal] = useState(false);
  const [hasCheckedForShift, setHasCheckedForShift] = useState(false);

  // Estado para evitar mostrar modal de apertura cuando hay un turno offline pendiente de autorización
  // null = aún no se ha verificado (no mostrar modal hasta que se complete la verificación)
  const [hasPendingLocalAuth, setHasPendingLocalAuth] = useState<boolean | null>(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasFetchedShift, setHasFetchedShift] = useState(false);

  // Obtener usuario del contexto si no se proporciona externamente
  const contextUser = getCurrentUser();
  const effectiveUserId = externalUserId || contextUser?.id;
  const prevShiftRef = useRef<any>(null);

  // Query para obtener turno actual
  const {
    data: currentShift,
    isLoading: shiftLoading,
    error: shiftError,
    refetch: refetchShift,
  } = useQuery({
    queryKey: ["currentShift", effectiveUserId],
    queryFn: async () => {
      console.log("🔍 [useShift] queryFn ejecutándose");

      if (!effectiveUserId) {
        console.log("⚠️ [useShift] No hay effectiveUserId");
        return null;
      }

      const shop = getCurrentShop();
      if (!shop) {
        console.log("⚠️ [useShift] No hay shop");
        return null;
      }

      let apiReachable = false;

      // 1. Intentar API primero
      try {
        console.log("🌐 [useShift] Intentando cargar turno desde API...");
        const shift = await shiftApiService.getCurrentShift(effectiveUserId);

        if (shift) {
          console.log("✅ [useShift] Turno cargado desde API:", shift.id);
          return shift;
        }

        // API respondió exitosamente pero no hay turno activo
        apiReachable = true;
        console.log(
          "📴 [useShift] API no devolvió turno, buscando en IndexedDB...",
        );
      } catch (apiError) {
        console.log("📴 [useShift] API falló, buscando en IndexedDB...");
      }

      // 2. Buscar en IndexedDB (tanto si API falló como si devolvió null)
      const offlineShift = await indexedDBService.getActiveOfflineShift(
        shop.id,
      );

      if (offlineShift) {
        // Si la API dijo "no hay turno" y el único disponible es un backup, está desactualizado
        if (apiReachable && offlineShift.isBackup) {
          await indexedDBService.deleteOfflineShift(offlineShift.id);
          console.log(
            "🗑️ [useShift] Backup de turno eliminado por desactualizado",
          );
          return null;
        }

        console.log(
          "✅ [useShift] Turno encontrado en IndexedDB:",
          offlineShift.id,
        );
        return offlineShift;
      }

      console.log("📭 [useShift] No hay turno activo");
      return null;
    },
    refetchInterval: isOfflineMode ? false : 30000, // {NUEVO: No refetch si offline}
    refetchOnWindowFocus: !isOfflineMode, // {NUEVO: No refetch si offline}
    staleTime: 10000,
    enabled: !!effectiveUserId,
  });

  // Query para obtener cajas registradoras
  const {
    data: cashRegisters = [],
    isLoading: cashRegistersLoading,
    error: cashRegistersError,
    refetch: refetchCashRegisters,
  } = useQuery({
    queryKey: ["cashRegisters"],
    queryFn: () => shiftApiService.getCashRegisters(),
  });

  // Detectar cuando la carga del turno termina por primera vez
  useEffect(() => {
    if (!shiftLoading && !hasFetchedShift) {
      console.log("✅ Primera carga del turno completada");
      setHasFetchedShift(true);
    }
  }, [shiftLoading, hasFetchedShift]);

  // Mutation para abrir turno
  const openShiftMutation = useMutation({
    mutationFn: ({
      cashRegisterId,
      initialCash,
    }: {
      cashRegisterId: string;
      initialCash: string;
    }) =>
      shiftApiService.openShift(cashRegisterId, initialCash, effectiveUserId),
    onSuccess: (data) => {
      console.log("🔵 [HOOK] onSuccess - Abriendo turno");

      queryClient.invalidateQueries({
        queryKey: ["currentShift", effectiveUserId],
      });

      setTimeout(() => {
        refetchShift();
      }, 100);

      setIsInitialLoad(true);
      setHasFetchedShift(false); // ← RESETEAR PARA LA PRÓXIMA CARGA

      setShowOpenShiftModal(false);
      setHasCheckedForShift(false);

      toast({
        title: "✅ Turno abierto",
        description: `Turno iniciado exitosamente`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "❌ Error al abrir turno",
        description: error.message || "No se pudo abrir el turno",
        variant: "destructive",
      });
    },
  });

  // Mutation para cerrar turno con diferencia
  const closeShiftMutation = useMutation({
    mutationFn: ({
      shiftId,
      countedCash,
      notes,
      denominationBreakdown,
      authorizationCode,
    }: {
      shiftId: string;
      countedCash: string;
      notes?: string;
      denominationBreakdown?: Record<string, number>;
      authorizationCode?: string;
    }) =>
      // {CORRECCIÓN: No enviar cashDifference ni discrepancyType - Caja ciega}
      shiftApiService
        .closeShift(
          shiftId,
          countedCash,
          notes,
          undefined, // cashDifference
          undefined, // discrepancyType
          denominationBreakdown,
          authorizationCode,
        )
        .catch((err: any) => {
          // {Propagar error con detalles del backend si existen}
          const details = err?.data?.details || "";
          const message = err?.message || "No se pudo cerrar el turno";
          const fullMessage = details || message;
          throw new Error(fullMessage);
        }),
    onSuccess: async (data) => {
      // {MODIFICADO: Recibir data para verificar estado del turno}
      console.log(
        "🔵 [HOOK] Turno cerrado exitosamente, actualizando estado...",
      );

      // {Esta sección verifica si el turno quedó pendiente de autorización para no limpiarlo prematuramente}
      const isPendingAuthorization =
        data?.shift?.status === "pending_authorization";

      // {NUEVO: Limpiar turno de IndexedDB solo si NO está pendiente de autorización}
      try {
        const shop = getCurrentShop();
        if (shop && currentShift?.id && !isPendingAuthorization) {
          await indexedDBService.deleteOfflineShift(currentShift.id);
          console.log("🗑️ [useShift] Turno eliminado de IndexedDB");
        }
      } catch (e) {
        console.warn(
          "⚠️ [useShift] No se pudo eliminar turno de IndexedDB:",
          e,
        );
      }

      // {NUEVO: Solo limpiar caché si el turno NO quedó pendiente de autorización}
      if (!isPendingAuthorization) {
        queryClient.setQueryData(["currentShift", effectiveUserId], null);
      }

      queryClient.invalidateQueries({
        queryKey: ["currentShift", effectiveUserId],
      });

      queryClient.invalidateQueries({ queryKey: ["shopSales"] });
      queryClient.invalidateQueries({ queryKey: ["shopInventory"] });

      setTimeout(() => {
        refetchShift();
      }, 100);

      setHasCheckedForShift(false);

      toast({
        title: "✅ Turno cerrado",
        description: "Turno cerrado exitosamente",
      });
    },
    onError: (error: any) => {
      console.error("❌ [use-shift] Error en closeShiftMutation:", error);
      console.log("📝 [use-shift] error.message:", error.message);
      toast({
        title: "❌ Error al cerrar turno",
        description: error.message || "No se pudo cerrar el turno",
        variant: "destructive",
      });
    },
  });

  // Función para abrir turno
  const openShift = useCallback(
    async (cashRegisterId: string, initialCash: string) => {
      const currentShop = getCurrentShop();
      const currentUser = getCurrentUser();

      if (!currentShop || !effectiveUserId) {
        throw new Error("No se puede abrir turno: falta información");
      }

      // {NUEVO: Si está offline, crear turno virtual}
      if (isOfflineMode) {
        console.log("📴 [OFFLINE] Creando turno virtual...");

        const offlineShift = {
          id: `offline_shift_${Date.now()}`,
          shopId: currentShop.id,
          cashRegisterId,
          employeeId: effectiveUserId,
          initialCash,
          openedBy: currentUser?.fullName || currentUser?.username || "Usuario",
          openedAt: new Date().toISOString(),
          status: "open",
          cashSales: "0.00",
          cardSales: "0.00",
          transferSales: "0.00",
          totalSales: "0.00",
          expectedCash: initialCash,
          synced: false,
        };

        await indexedDBService.saveOfflineShift(offlineShift);

        // Actualizar el estado local
        queryClient.setQueryData(
          ["currentShift", effectiveUserId],
          offlineShift,
        );

        toast({
          title: "📴 Turno offline abierto",
          description: "El turno se sincronizará cuando vuelva la conexión",
        });

        setShowOpenShiftModal(false);

        return offlineShift;
      }

      // Online: usar API normal
      return openShiftMutation.mutateAsync({ cashRegisterId, initialCash });
    },
    [openShiftMutation, isOfflineMode, effectiveUserId, queryClient],
  );

  // Función para cerrar turno con cálculo de diferencia
  const closeShift = useCallback(
    async (
      shiftId: string,
      countedCash: string,
      notes?: string,
      denominationBreakdown?: Record<string, number>,
      authorizationCode?: string,
    ) => {
      // {CORRECCIÓN: NO calcular diferencia en frontend - Caja ciega}
      // Simplemente pasar los datos al backend
      return closeShiftMutation.mutateAsync({
        shiftId,
        countedCash,
        notes,
        denominationBreakdown,
        authorizationCode,
      });
    },
    [closeShiftMutation],
  );

  // Forzar recarga de cajas
  const reloadCashRegisters = useCallback(async () => {
    await refetchCashRegisters();
  }, [refetchCashRegisters]);

  // Efecto para mostrar modal automáticamente de apertura de turno
  useEffect(() => {
    if (!hasFetchedShift) {
      console.log("⏳ Esperando primera carga del turno...");
      return;
    }

    // {NUEVO: Esperar verificación de turno offline pendiente de autorización}
    if (hasPendingLocalAuth === null) {
      console.log("⏳ Esperando verificación de turno offline pendiente...");
      return;
    }

    // {NUEVO: No mostrar modal si hay un turno offline pendiente de autorización}
    if (hasPendingLocalAuth) {
      console.log("🔒 Hay turno offline pendiente de autorización, no mostrar apertura");
      return;
    }

    // {NUEVO: No mostrar modal si estamos offline — esperar sincronización}
    if (isOfflineMode) {
      console.log("📴 [useShift] Modo offline, no mostrar apertura");
      return;
    }

    if (currentShift) {
      console.log("✅ Turno activo detectado, no mostrar modal de apertura");
      return;
    }

    const shouldShowModal =
      cashRegisters.length > 0 &&
      !cashRegistersLoading &&
      !hasCheckedForShift &&
      !showOpenShiftModal;

    if (shouldShowModal) {
      console.log("📍 Mostrando modal de apertura automáticamente");
      setShowOpenShiftModal(true);
      setHasCheckedForShift(true);
    }
  }, [
    hasFetchedShift,
    currentShift,
    cashRegisters,
    cashRegistersLoading,
    hasCheckedForShift,
    showOpenShiftModal,
    hasPendingLocalAuth,
    isOfflineMode,
  ]);

  // Verificar turno offline pendiente de autorización
  useEffect(() => {
    const shop = getCurrentShop();
    if (shop) {
      console.log(
        "🔄 [useShift] Reseteando banderas al cambiar de usuario/tienda",
      );
      setHasPendingLocalAuth(null);
      setIsInitialLoad(true);
      setHasFetchedShift(false);
    }
  }, [effectiveUserId]);

  // {NUEVO: Verificar si hay turno offline pendiente de autorización para no mostrar modal de apertura}
  // Se re-evalúa cuando cambia el usuario o cuando se completa una consulta de turno (post-sincronización)
  useEffect(() => {
    const checkPendingAuth = async () => {
      const shop = getCurrentShop();
      if (!shop) return;
      try {
        const pendingAuth = await indexedDBService.getPendingAuthorizationOfflineShift(shop.id);
        setHasPendingLocalAuth(!!pendingAuth);
      } catch {
        setHasPendingLocalAuth(false);
      }
    };
    checkPendingAuth();
  }, [effectiveUserId, currentShift]);

  // {NUEVO: Cuando hay un turno ONLINE activo, guardarlo en IndexedDB como respaldo}
  useEffect(() => {
    const shop = getCurrentShop();

    // {CORRECCIÓN: SOLO guardar backup si el turno es ONLINE (no comienza con "offline_shift_")}
    const isOnlineShift =
      currentShift && !currentShift.id?.startsWith("offline_shift_");

    if (
      currentShift &&
      !isOfflineMode &&
      currentShift.status === "open" &&
      shop &&
      isOnlineShift
    ) {
      // Guardar una copia en IndexedDB por si se pierde la conexión
      const backupShift = {
        id: currentShift.id,
        realId: currentShift.id, // ← El realId es el mismo ID del backend
        shopId: shop.id,
        cashRegisterId: currentShift.cashRegisterId,
        employeeId: currentShift.employeeId,
        initialCash: currentShift.initialCash,
        openedBy: currentShift.openedBy,
        openedAt: currentShift.openedAt,
        status: currentShift.status,
        cashSales: currentShift.cashSales,
        cardSales: currentShift.cardSales,
        transferSales: currentShift.transferSales,
        totalSales: currentShift.totalSales,
        expectedCash: currentShift.expectedCash,
        isBackup: true,
        synced: true,
        backedUpAt: new Date().toISOString(),
      };
      indexedDBService.saveOfflineShift(backupShift).catch((err) => {
        console.error("Error guardando respaldo de turno:", err);
      });
      console.log(
        "💾 [BACKUP] Turno online respaldado en IndexedDB con realId:",
        currentShift.id,
      );
    }
  }, [currentShift, isOfflineMode]);

  // {NUEVO: Resetear hasCheckedForShift cuando el turno se cierra (transición truthy → null)}
  useEffect(() => {
    if (prevShiftRef.current && !currentShift) {
      console.log("🔄 [useShift] Turno cerrado, permitiendo modal de apertura");
      setHasCheckedForShift(false);
    }
    prevShiftRef.current = currentShift;
  }, [currentShift]);

  return {
    // Estado
    currentShift,
    cashRegisters,
    shiftLoading,
    cashRegistersLoading,
    showOpenShiftModal,

    // Acciones
    setShowOpenShiftModal,
    openShift,
    closeShift,
    refetchShift,
    reloadCashRegisters,

    // Estados de mutaciones
    isOpeningShift: openShiftMutation.isPending,
    isClosingShift: closeShiftMutation.isPending,

    // Helpers
    hasActiveShift: !!currentShift,
    shouldShowModal: !currentShift && cashRegisters.length > 0,

    // Errores
    shiftError,
    cashRegistersError,
  };
}
